/**
 * Pager
 *
 * Shared paging control for the List & Grid Standard — right-aligned page indicator
 * plus prev/next chevron buttons, pinned below the grid via `ListPageLayout`'s footer.
 * See `.github/context/list-grid-standard.context.md` §9.
 */
import type { FC } from 'react';
import { Button, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronLeft24Regular, ChevronRight24Regular } from '@fluentui/react-icons';

/** Industry-standard admin-grid default page size (ratified 2026-07-09). */
export const PAGE_SIZE = 25;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalM,
  },
  info: {
    color: tokens.colorNeutralForeground3,
  },
});

export interface PagerProps {
  page: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  totalCount: number;
  onPageChange: (page: number) => void;
  testId?: string;
}

export const Pager: FC<PagerProps> = ({
  page,
  totalPages,
  hasPreviousPage,
  hasNextPage,
  totalCount,
  onPageChange,
  testId,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.root} data-testid={testId}>
      <Caption1 className={styles.info} data-testid={testId ? `${testId}-info` : undefined}>
        Page {page} of {Math.max(totalPages, 1)} · {totalCount} total
      </Caption1>
      <Button
        appearance="subtle"
        icon={<ChevronLeft24Regular />}
        aria-label="Previous page"
        disabled={!hasPreviousPage}
        onClick={() => onPageChange(page - 1)}
        data-testid={testId ? `${testId}-prev` : undefined}
      />
      <Button
        appearance="subtle"
        icon={<ChevronRight24Regular />}
        aria-label="Next page"
        disabled={!hasNextPage}
        onClick={() => onPageChange(page + 1)}
        data-testid={testId ? `${testId}-next` : undefined}
      />
    </div>
  );
};
